import type {
  ArrivalImportRow,
  AuthUser,
  CreateBatchInput,
  CreateSkuInput,
  CreateUserInput,
  DashboardStats,
  ItemStatus,
  LocationBatchInput,
  LocationDeleteInput,
  LocationGenerateInput,
  LocationImportRow,
  OpeningImportRow,
  OutboundImportRow,
  PackScanResult,
  ReconciliationInput,
  RoleCode,
  SkuImportRow,
  StockSkuList,
  TraceTimelineResponse,
  UnitListRow,
} from "@shoe-trace/shared";

const TOKEN_KEY = "wms.token";
const USER_KEY = "wms.user";

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const unauthorizedListeners = new Set<() => void>();

export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export function readStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as AuthUser;
  } catch {
    return null;
  }
}

export function storeSession(token: string, user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function token(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  form?: FormData;
  skipAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers();
  const auth = options.skipAuth ? null : token();
  if (auth) headers.set("Authorization", `Bearer ${auth}`);
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }
  let response: Response;
  try {
    response = await fetch(path, { method: options.method ?? "GET", headers, body });
  } catch {
    throw new ApiError(0, "现在连不上系统。请看右上角状态，连上后再试一次。");
  }
  if (response.status === 401 && auth) {
    clearSession();
    unauthorizedListeners.forEach((listener) => listener());
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const payload: unknown = text ? parseJson(text) : null;
  if (!response.ok) {
    throw new ApiError(response.status, messageOf(payload));
  }
  return payload as T;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function messageOf(payload: unknown): string {
  if (payload && typeof payload === "object" && "message" in payload) {
    const message = payload.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return "这一步没有完成。请稍后再试一次。";
}

export async function downloadFile(path: string, filename: string): Promise<void> {
  const headers = new Headers();
  const auth = token();
  if (auth) headers.set("Authorization", `Bearer ${auth}`);
  const response = await fetch(path, { headers });
  if (response.status === 401 && auth) {
    clearSession();
    unauthorizedListeners.forEach((listener) => listener());
  }
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, messageOf(parseJson(text)));
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export interface LoginResult {
  token: string;
  user: AuthUser;
}

export function login(employeeNo: string, password: string): Promise<LoginResult> {
  return request<LoginResult>("/api/auth/login", {
    method: "POST",
    skipAuth: true,
    body: { employeeNo, password, client: "console" },
  });
}

export function logout(): Promise<{ ok: boolean }> {
  return request("/api/auth/logout", { method: "POST" });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<{ user: AuthUser }> {
  return request("/api/auth/change-password", {
    method: "POST",
    body: { currentPassword, newPassword },
  });
}

export function fetchMe(): Promise<{ user: AuthUser }> {
  return request("/api/auth/me");
}

export function fetchHealth(): Promise<{ ok: boolean }> {
  return request("/api/health", { skipAuth: true });
}

export interface TodoCounts {
  printTasks: number;
  unlocated: number;
  remaining: number;
  reconcile: number;
  pendingApprovals: number;
}

export function fetchTodos(): Promise<TodoCounts> {
  return request("/api/dashboard/todos");
}

export function fetchDashboard(): Promise<DashboardStats> {
  return request("/api/dashboard/stats");
}

export function fetchStockBySku(queryText: string): Promise<StockSkuList> {
  return request(`/api/stock/skus${query({ q: queryText.trim() })}`);
}

export interface InterceptReport {
  day: string;
  orderNo: string;
  blocked: number;
  passed: number;
}

export interface WorkloadReport {
  day: string;
  operator: string;
  activated: number;
  putaway: number;
  verified: number;
}

export function fetchIntercepts(from?: string, to?: string): Promise<InterceptReport[]> {
  return request(`/api/reports/intercepts${query({ from, to })}`);
}

export function fetchWorkload(from?: string, to?: string): Promise<WorkloadReport[]> {
  return request(`/api/reports/workload${query({ from, to })}`);
}

function query(values: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value) params.set(key, value);
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}

export interface SkuRow {
  id: string;
  humanCode: string;
  name: string;
  color: string;
  sizeUs: string;
  sizeEu: string;
  trackUnit: boolean;
  internalCode: string;
}

export function searchSkus(q: string, page = 1, pageSize = 20): Promise<{ total: number; rows: SkuRow[] }> {
  return request(`/api/skus${query({ q, page: String(page), pageSize: String(pageSize) })}`);
}

export function createSku(input: CreateSkuInput): Promise<{ id: string; internalCode: string }> {
  return request("/api/skus", { method: "POST", body: input });
}

export function updateSku(id: string, input: CreateSkuInput): Promise<{ ok: boolean }> {
  return request(`/api/skus/${id}`, { method: "PATCH", body: input });
}

export interface BatchRow {
  id: string;
  batchNo: string;
  skuId: string;
  humanCode: string;
  inboundDate: string;
  factoryName: string | null;
  plannedQty: number;
  activatedQty: number;
  status: string;
  qcNote: string | null;
  unlocated: number;
}

export function listBatches(
  q = "",
  page = 1,
  pageSize = 20
): Promise<{ total: number; rows: BatchRow[] }> {
  return request(`/api/batches${query({ q, page: String(page), pageSize: String(pageSize) })}`);
}

export function createBatch(input: CreateBatchInput): Promise<{ id: string; batchNo: string }> {
  return request("/api/batches", { method: "POST", body: input });
}

export interface ReadyBatch {
  id: string;
  batchNo: string;
  humanCode: string;
  inboundDate: string;
}

export function readyBatches(): Promise<ReadyBatch[]> {
  return request("/api/batches/ready");
}

export interface LocationCard {
  id: string;
  code: string;
  capacity: number;
  status: "NORMAL" | "FULL" | "LOCKED";
  occupied: number;
  removable: boolean;
  groupKey: string | null;
  arrivalNo: string | null;
  batches: Array<{ batchNo: string; count: number }>;
}

export interface LocationList {
  total: number;
  rows: LocationCard[];
  zones: string[];
  racks: string[];
  shelves: string[];
  empty: number;
  kept: number;
}

export interface LocationScope {
  zone?: string;
  rack?: string;
  shelf?: string;
}

export function listLocations(filter: LocationScope & { code?: string; q?: string } = {}): Promise<LocationList> {
  return request(`/api/locations${query({
    zone: filter.zone,
    rack: filter.rack,
    shelf: filter.shelf,
    code: filter.code,
    q: filter.q,
  })}`);
}

export function previewLocationGenerate(input: LocationGenerateInput): Promise<{
  total: number;
  created: number;
  skipped: number;
  message: string;
}> {
  return request("/api/locations/generate/preview", { method: "POST", body: input });
}

export function generateLocations(input: LocationGenerateInput): Promise<{
  total: number;
  created: number;
  skipped: number;
  message: string;
}> {
  return request("/api/locations/generate", { method: "POST", body: input });
}

export function batchLocations(input: LocationBatchInput): Promise<{ updated: number }> {
  return request("/api/locations/batch", { method: "POST", body: input });
}

export function deleteLocations(input: LocationDeleteInput): Promise<{ deleted: number; kept: number }> {
  return request("/api/locations/delete", { method: "POST", body: input });
}

export function updateLocation(
  code: string,
  input: { capacity: number; status: "NORMAL" | "FULL" | "LOCKED"; password: string }
): Promise<{ ok: boolean }> {
  return request(`/api/locations/${code}`, { method: "PATCH", body: input });
}

export function correctLocation(input: {
  batchId: string;
  newLocationCode: string;
  password: string;
}): Promise<{ locationCode: string; bound: number }> {
  return request("/api/locations/correct", { method: "POST", body: input });
}

export function putaway(locationCode: string, batchId: string): Promise<{ locationCode: string; bound: number; left: number }> {
  return request("/api/putaway", { method: "POST", body: { locationCode, batchId } });
}

export interface ImportIssue {
  row: number;
  message: string;
}

export interface ImportPreview<T> {
  total: number;
  rows: T[];
  issues: ImportIssue[];
  created?: number;
  skipped?: number;
  rejected?: number;
}

export type ImportKind = "sku" | "location" | "opening" | "arrival" | "outbound";

export function previewImport(kind: ImportKind, file: File): Promise<
  ImportPreview<SkuImportRow | LocationImportRow | OpeningImportRow | ArrivalImportRow | OutboundImportRow> & {
    header?: { channel: string; targetWarehouse: string } | null;
  }
> {
  const form = new FormData();
  form.set("kind", kind);
  form.set("file", file);
  return request("/api/imports/preview", { method: "POST", form });
}

export function commitImport(body: {
  kind: "sku" | "location" | "opening";
  rows: Array<SkuImportRow | LocationImportRow | OpeningImportRow>;
}): Promise<{ written: number; skipped: number }> {
  return request("/api/imports/commit", { method: "POST", body });
}

export function commitArrival(rows: ArrivalImportRow[]): Promise<{ arrivalNo: string; lineCount: number; totalQty: number }> {
  return request("/api/imports/commit", { method: "POST", body: { kind: "arrival", rows } });
}

export function commitOutbound(rows: OutboundImportRow[]): Promise<{ orderNo: string; lineCount: number; totalQty: number }> {
  return request("/api/imports/commit", { method: "POST", body: { kind: "outbound", rows } });
}

export interface ArrivalSummary {
  arrivalNo: string;
  status: string;
  lineCount: number;
  plannedQty: number;
  createdAt: string;
}

export interface ArrivalLineRow {
  lineNo: number;
  humanCode: string;
  plannedQty: number;
  inboundDate: string;
  batchNo: string;
}

export interface ArrivalDetail extends ArrivalSummary {
  spareRatio: number | null;
  plannedQtys: number[];
  lineTotal: number;
  lines: ArrivalLineRow[];
}

export function listArrivals(page = 1, pageSize = 20, work = false): Promise<{ total: number; rows: ArrivalSummary[] }> {
  return request(`/api/arrivals${query({ page: String(page), pageSize: String(pageSize), work: work ? "1" : undefined })}`);
}

export function getArrival(arrivalNo: string, page = 1, pageSize = 50, qtys = false): Promise<ArrivalDetail> {
  return request(
    `/api/arrivals/${encodeURIComponent(arrivalNo)}${query({
      page: String(page),
      pageSize: String(pageSize),
      qtys: qtys ? "1" : undefined,
    })}`
  );
}

export interface ArrivalProgress {
  arrivalNo: string;
  status: string;
  printed: number;
  activated: number;
  voided: number;
  remaining: number;
  lineTotal: number;
  lines: Array<{
    humanCode: string;
    plannedQty: number;
    printed: number;
    activated: number;
    voided: number;
    remaining: number;
  }>;
  preview: Array<{ humanCode: string; internalCode: string; serial: string; gs1: string }>;
}

export function lockArrival(arrivalNo: string, spareRatio: number): Promise<{ runNo: string; taskCount: number; arrivalNo: string }> {
  return request(`/api/arrivals/${encodeURIComponent(arrivalNo)}/lock`, { method: "POST", body: { spareRatio } });
}

export function arrivalProgress(arrivalNo: string, page = 1, pageSize = 50): Promise<ArrivalProgress> {
  return request(
    `/api/arrivals/${encodeURIComponent(arrivalNo)}/progress${query({ page: String(page), pageSize: String(pageSize) })}`
  );
}

export function closeArrival(arrivalNo: string): Promise<{ ok: boolean }> {
  return request(`/api/arrivals/${encodeURIComponent(arrivalNo)}/close`, { method: "POST" });
}

export interface PutawayTarget {
  batchId: string;
  batchNo: string;
  humanCode: string;
  pendingQty: number;
  locationCode: string | null;
  arrivalNo: string | null;
  groupKey: string;
}

export function resolvePutaway(scannedCode: string): Promise<PutawayTarget> {
  return request("/api/putaway/resolve", { method: "POST", body: { scannedCode } });
}

export interface PrintUnit {
  serial: string;
  gs1: string;
  status: ItemStatus;
  replacesSerial: string | null;
  replacedBySerial: string | null;
}

export interface PrintTask {
  id: string;
  taskNo: string;
  batchId: string;
  batchNo: string;
  skuId: string;
  humanCode: string;
  name: string;
  internalCode: string;
  quantity: number;
  arrivalQty: number;
  spareRatio: number;
  status: string;
  serialFrom: string;
  serialTo: string;
  printRunId: string | null;
  units: PrintUnit[];
}

export interface CreatedTask {
  id: string;
  taskNo: string;
  serialFrom: string;
  serialTo: string;
  quantity: number;
}

export function listPrintTasks(legacy = false): Promise<PrintTask[]> {
  return request(`/api/print-tasks${legacy ? "?legacy=1" : ""}`);
}

export function getPrintTask(id: string): Promise<PrintTask> {
  return request(`/api/print-tasks/${id}`);
}

export function closePrintTask(id: string): Promise<{ ok: boolean }> {
  return request(`/api/print-tasks/${id}/close`, { method: "POST" });
}

export interface PrintLogRow {
  id: string;
  createdAt: string;
  operatorName: string;
  template: string;
  objectRef: string;
  rangeText: string;
  copies: number;
  taskNo: string | null;
}

export function listPrintLogs(): Promise<PrintLogRow[]> {
  return request("/api/print-logs");
}

export function activateUnit(
  scannedCode: string,
  scope?: { printTaskId?: string; arrivalNo?: string }
): Promise<{ serial: string; gs1: string; status: string; humanCode: string; batchNo: string; message: string }> {
  return request("/api/units/activate", { method: "POST", body: { scannedCode, ...scope } });
}

export function requestVoid(serial: string, reason: string): Promise<{ id: string; requestNo: string }> {
  return request("/api/void-requests", { method: "POST", body: { serial, reason } });
}

export interface OutboundLineView {
  skuId: string;
  humanCode: string;
  requiredQty: number;
  verifiedQty: number;
}

export interface OutboundOrder {
  orderNo: string;
  channel: string;
  targetWarehouse: string | null;
  trackingNo: string | null;
  totalQty: number;
  verifiedQty: number;
  status: string;
  lineCount: number;
  lines: OutboundLineView[];
}

export interface PickLine {
  seq: number;
  locationCode: string;
  batchNo: string;
  inboundDate: string;
  humanCode: string;
  qty: number;
  ageDays: number;
  note: string | null;
}

export function listOrders(): Promise<OutboundOrder[]> {
  return request("/api/outbound-orders");
}

export function getOrder(orderNo: string): Promise<OutboundOrder> {
  return request(`/api/outbound-orders/${encodeURIComponent(orderNo)}`);
}

export function findOrder(scannedCode: string): Promise<OutboundOrder> {
  return request("/api/outbound-orders/find", { method: "POST", body: { scannedCode } });
}

export function fetchPickList(orderNo: string): Promise<{ order: OutboundOrder; lines: PickLine[] }> {
  return request(`/api/outbound-orders/${orderNo}/pick-list`);
}

export function verifyScan(orderNo: string, scannedCode: string): Promise<PackScanResult> {
  return request(`/api/outbound-orders/${orderNo}/verify`, { method: "POST", body: { scannedCode } });
}

export function shipOrder(orderNo: string): Promise<{ orderNo: string; trackingNo: string | null }> {
  return request(`/api/outbound-orders/${orderNo}/ship`, { method: "POST", body: {} });
}

export type LookupResult =
  | { mode: "one"; item: TraceTimelineResponse }
  | { mode: "many"; rows: UnitListRow[] }
  | { mode: "none" };

export function lookupUnits(by: string, q: string): Promise<LookupResult> {
  return request(`/api/units/lookup${query({ by, q })}`);
}

export function fetchTimeline(serial: string): Promise<TraceTimelineResponse> {
  return request(`/api/units/${serial}/timeline`);
}

export interface VoidRow {
  id: string;
  requestNo: string;
  serial: string;
  reason: string;
  status: string;
  applicantName: string;
  applicantId: string;
  newSerial: string | null;
  createdAt: string;
}

export interface FreezeRow {
  id: string;
  requestNo: string;
  serial: string;
  reason: string;
  note: string | null;
  status: string;
  action: string;
  applicantName: string;
  applicantId: string;
  createdAt: string;
}

export interface RehangRow {
  id: string;
  requestNo: string;
  oldSerial: string | null;
  newSerial: string | null;
  method: string;
  locationCode: string | null;
  batchNo: string | null;
  note: string | null;
  status: string;
  applicantName: string;
  applicantId: string;
  createdAt: string;
}

export interface BlockRow {
  id: string;
  rawCode: string;
  orderNo: string | null;
  serial: string | null;
  reason: string | null;
  operatorName: string;
  createdAt: string;
  releasedQty: number;
}

export function listExceptions(): Promise<{
  voids: VoidRow[];
  freezes: FreezeRow[];
  rehangs: RehangRow[];
  blocks: BlockRow[];
}> {
  return request("/api/exceptions");
}

export function approveVoid(id: string, password: string): Promise<{ newSerial: string; oldSerial: string }> {
  return request(`/api/void-requests/${id}/approve`, { method: "POST", body: { password } });
}

export function rejectVoid(id: string, reason: string): Promise<{ ok: boolean }> {
  return request(`/api/void-requests/${id}/reject`, { method: "POST", body: { reason } });
}

export function createRehang(body: {
  scannedCode?: string;
  locationCode?: string;
  batchId?: string;
  serial?: string;
  note?: string;
}): Promise<{ id: string }> {
  return request("/api/rehang-requests", { method: "POST", body });
}

export function approveRehang(id: string, password: string): Promise<{ oldSerial: string; newSerial: string }> {
  return request(`/api/rehang-requests/${id}/approve`, { method: "POST", body: { password } });
}

export function noteMissing(reason: string): Promise<{ id: string }> {
  return request("/api/rehang-requests/note", { method: "POST", body: { reason } });
}

export interface ReturnInspect {
  kind: "missing" | "found";
  serial?: string;
  status?: ItemStatus;
  humanCode?: string;
  shippedAt?: string | null;
  trackingNo?: string | null;
  batchNo?: string;
}

export function inspectReturn(scannedCode: string): Promise<ReturnInspect> {
  return request("/api/returns/verify", { method: "POST", body: { scannedCode } });
}

export function unitsByTracking(trackingNo: string): Promise<
  Array<{ serial: string; humanCode: string; status: ItemStatus; locationCode: string | null; shippedAt: string | null }>
> {
  return request(`/api/returns/tracking${query({ trackingNo })}`);
}

export function markReturned(serial: string): Promise<{ ok: boolean }> {
  return request(`/api/returns/${serial}/mark`, { method: "POST" });
}

export function noteForeign(note: string, trackingNo?: string): Promise<{ ok: boolean }> {
  return request("/api/returns/foreign", { method: "POST", body: { note, trackingNo } });
}

export function freezeUnit(serial: string, reason: string, password: string, note?: string): Promise<{ ok: boolean }> {
  return request(`/api/units/${serial}/freeze`, { method: "POST", body: { reason, password, note } });
}

export function unfreezeUnit(serial: string, password: string): Promise<{ ok: boolean }> {
  return request(`/api/units/${serial}/unfreeze`, { method: "POST", body: { password } });
}

export function requestFreeze(serial: string, reason: string, note?: string): Promise<{ id: string; requestNo: string }> {
  return request("/api/freeze-requests", { method: "POST", body: { serial, reason, note } });
}

export function approveFreeze(id: string, password: string): Promise<{ ok: boolean }> {
  return request(`/api/freeze-requests/${id}/approve`, { method: "POST", body: { password } });
}

export interface StockOpen {
  id: string;
  countNo: string;
  locationCode: string;
  batchNo: string | null;
  expected: Array<{ serial: string; humanCode: string; batchNo: string; status: ItemStatus }>;
}

export function openStockCount(locationCode: string): Promise<StockOpen> {
  return request("/api/stock-counts", { method: "POST", body: { locationCode } });
}

export interface StockScan {
  duplicate: boolean;
  serial: string;
  kind?: string;
  note?: string | null;
  humanCode?: string | null;
  batchNo?: string | null;
  message?: string | null;
}

export function scanStockCount(id: string, scannedCode: string): Promise<StockScan> {
  return request(`/api/stock-counts/${id}/scan`, { method: "POST", body: { scannedCode } });
}

export interface StockClose {
  expected: number;
  scanned: number;
  missing: string[];
  foreign: string[];
  unknown: string[];
  matched: string[];
}

export function closeStockCount(id: string): Promise<StockClose> {
  return request(`/api/stock-counts/${id}/close`, { method: "POST" });
}

export interface AdjustmentRow {
  id: string;
  adjustNo: string;
  locationCode: string | null;
  kind: string;
  note: string;
  serials: string[];
  status: string;
  applicantId: string;
  applicantName: string;
  createdAt: string;
}

export function listAdjustments(): Promise<AdjustmentRow[]> {
  return request("/api/adjustments");
}

export function createAdjustment(body: {
  stockCountId?: string;
  locationCode?: string;
  kind: "SHORTAGE" | "DAMAGE" | "SURPLUS";
  note: string;
  serials: string[];
}): Promise<{ id: string; adjustNo: string }> {
  return request("/api/adjustments", { method: "POST", body });
}

export function approveAdjustment(id: string, password: string): Promise<{ changed: number }> {
  return request(`/api/adjustments/${id}/approve`, { method: "POST", body: { password } });
}

export function rejectAdjustment(id: string, reason: string): Promise<{ ok: boolean }> {
  return request(`/api/adjustments/${id}/reject`, { method: "POST", body: { reason } });
}

export interface ReconciliationRow {
  id: string;
  weekKey: string;
  systemQty: number;
  physicalQty: number;
  odooQty: number;
  gapPhysical: number;
  gapOdoo: number;
  reason: string | null;
  resolved: boolean;
  resolution: string | null;
  operatorName: string;
  createdAt: string;
}

export function listReconciliations(): Promise<ReconciliationRow[]> {
  return request("/api/reconciliations");
}

export function systemQty(): Promise<{ systemQty: number }> {
  return request("/api/reconciliations/system-qty");
}

export function saveReconciliation(input: ReconciliationInput): Promise<ReconciliationRow> {
  return request("/api/reconciliations", { method: "POST", body: input });
}

export interface AccountRow {
  id: string;
  employeeNo: string;
  name: string;
  role: RoleCode;
  roleLabel: string;
  status: string;
  lastLoginAt: string | null;
}

export function listUsers(): Promise<AccountRow[]> {
  return request("/api/users");
}

export function createUser(input: CreateUserInput): Promise<{ id: string }> {
  return request("/api/users", { method: "POST", body: input });
}

export function disableUser(id: string): Promise<{ ok: boolean }> {
  return request(`/api/users/${id}/disable`, { method: "POST" });
}

export function resetPassword(id: string, password: string): Promise<{ ok: boolean }> {
  return request(`/api/users/${id}/reset-password`, { method: "POST", body: { password } });
}

export function renameUser(id: string, name: string): Promise<{ id: string; name: string }> {
  return request(`/api/users/${encodeURIComponent(id)}`, { method: "PATCH", body: { name } });
}

export function changeUserRole(id: string, role: RoleCode): Promise<{ id: string; role: RoleCode; roleLabel: string }> {
  return request(`/api/users/${encodeURIComponent(id)}/role`, { method: "POST", body: { role } });
}
